import type {Editor} from '../../interfaces/editor'
import {DOMEditor} from '../plugin/dom-editor'

/**
 * Types.
 */

// COMPAT: This is required to prevent TypeScript aliases from doing some very
// weird things for the engine's types with the same name as globals. (2019/11/27)
// https://github.com/microsoft/TypeScript/issues/35002
type DOMNode = globalThis.Node
type DOMComment = globalThis.Comment
type DOMElement = globalThis.Element
// DOMText is used as a value (instanceof) in dom-editor.ts, so we need both
// the type alias and a const reference to the global constructor.
type DOMText = globalThis.Text
const DOMText = globalThis.Text
type DOMRange = globalThis.Range
type DOMSelection = globalThis.Selection
type DOMStaticRange = globalThis.StaticRange

export {
  type DOMNode,
  type DOMElement,
  DOMText,
  type DOMRange,
  type DOMSelection,
  type DOMStaticRange,
}

declare global {
  interface Window {
    Selection: (typeof Selection)['constructor']
    DataTransfer: (typeof DataTransfer)['constructor']
    Node: (typeof Node)['constructor']
  }
}

export type DOMPoint = [Node, number]

/**
 * Returns the host window of a DOM node
 */

export const getDefaultView = (value: any): Window | null => {
  return (
    (value && value.ownerDocument && value.ownerDocument.defaultView) || null
  )
}

/**
 * Check if a DOM node is a comment node.
 */

const isDOMComment = (value: any): value is DOMComment => {
  return isDOMNode(value) && value.nodeType === 8
}

/**
 * Check if a DOM node is an element node.
 */

export const isDOMElement = (value: any): value is DOMElement => {
  return isDOMNode(value) && value.nodeType === 1
}

/**
 * Check if a value is a DOM node.
 */

export const isDOMNode = (value: any): value is DOMNode => {
  const window = getDefaultView(value)
  return !!window && value instanceof window.Node
}

/**
 * Check if a value is a DOM selection.
 */

export const isDOMSelection = (value: any): value is DOMSelection => {
  const window = value && value.anchorNode && getDefaultView(value.anchorNode)
  return !!window && value instanceof window.Selection
}

/**
 * Checks whether a paste event is a plaintext-only event.
 */

export const isPlainTextOnlyPaste = (event: ClipboardEvent) => {
  return (
    event.clipboardData &&
    event.clipboardData.getData('text/plain') !== '' &&
    event.clipboardData.types.length === 1
  )
}

/**
 * Normalize a DOM point so that it always refers to a text node.
 */

export const normalizeDOMPoint = (domPoint: DOMPoint): DOMPoint => {
  let [node, offset] = domPoint

  // If it's an element node, its offset refers to the index of its children
  // including comment nodes, so try to find the right text child node.
  if (isDOMElement(node) && node.childNodes.length) {
    let isLast = offset === node.childNodes.length
    let index = isLast ? offset - 1 : offset
    ;[node, index] = getEditableChildAndIndex(
      node,
      index,
      isLast ? 'backward' : 'forward',
    )
    // If the editable child found is in front of input offset, we instead seek to its end
    isLast = index < offset

    // If the node has children, traverse until we have a leaf node. Leaf nodes
    // can be either text nodes, or other void DOM nodes.
    while (isDOMElement(node) && node.childNodes.length) {
      const i = isLast ? node.childNodes.length - 1 : 0
      node = getEditableChild(node, i, isLast ? 'backward' : 'forward')
    }

    // Determine the new offset inside the text node.
    offset = isLast && node.textContent != null ? node.textContent.length : 0
  }

  // Return the node and offset.
  return [node, offset]
}

/**
 * Determines whether the active element is nested within a shadowRoot
 */

export const hasShadowRoot = (node: Node | null) => {
  let parent = node && node.parentNode
  while (parent) {
    if (parent.toString() === '[object ShadowRoot]') {
      return true
    }
    parent = parent.parentNode
  }
  return false
}

/**
 * Get the nearest editable child and index at `index` in a `parent`, preferring
 * `direction`.
 */

const getEditableChildAndIndex = (
  parent: DOMElement,
  index: number,
  direction: 'forward' | 'backward',
): [DOMNode, number] => {
  const {childNodes} = parent
  let child: ChildNode | undefined = childNodes[index]
  let i = index
  let triedForward = false
  let triedBackward = false

  // While the child is a comment node, or an element node with no children,
  // keep iterating to find a sibling non-void, non-comment node.
  while (
    isDOMComment(child) ||
    (isDOMElement(child) && child.childNodes.length === 0) ||
    (isDOMElement(child) && child.getAttribute('contenteditable') === 'false')
  ) {
    if (triedForward && triedBackward) {
      break
    }

    if (i >= childNodes.length) {
      triedForward = true
      i = index - 1
      direction = 'backward'
      continue
    }

    if (i < 0) {
      triedBackward = true
      i = index + 1
      direction = 'forward'
      continue
    }

    child = childNodes[i]
    index = i
    i += direction === 'forward' ? 1 : -1
  }

  return [child!, index]
}

/**
 * Get the nearest editable child at `index` in a `parent`, preferring
 * `direction`.
 */

const getEditableChild = (
  parent: DOMElement,
  index: number,
  direction: 'forward' | 'backward',
): DOMNode => {
  const [child] = getEditableChildAndIndex(parent, index, direction)
  return child
}

/**
 * Get the dom selection from Shadow Root if possible, otherwise from the document
 */
export const getSelection = (root: Document | ShadowRoot): Selection | null => {
  if ('getSelection' in root && typeof root.getSelection === 'function') {
    return root.getSelection()
  }
  return document.getSelection()
}

/**
 * Check whether a mutation originates from a editable element inside the editor.
 */

export const isTrackedMutation = (
  editor: Editor,
  mutation: MutationRecord,
  batch: MutationRecord[],
): boolean => {
  const {target} = mutation
  if (isDOMElement(target) && target.matches('[contentEditable="false"]')) {
    return false
  }

  const {document} = DOMEditor.getWindow(editor)
  if (containsShadowAware(document, target)) {
    return DOMEditor.hasDOMNode(editor, target, {editable: true})
  }

  const parentMutation = batch.find(({addedNodes, removedNodes}) => {
    for (const node of addedNodes) {
      if (node === target || containsShadowAware(node, target)) {
        return true
      }
    }

    for (const node of removedNodes) {
      if (node === target || containsShadowAware(node, target)) {
        return true
      }
    }

    return false
  })

  if (!parentMutation || parentMutation === mutation) {
    return false
  }

  // Target add/remove is tracked. Track the mutation if we track the parent mutation.
  return isTrackedMutation(editor, parentMutation, batch)
}

/**
 * Retrieves the deepest active element in the DOM, considering nested shadow DOMs.
 */
export const getActiveElement = () => {
  let activeElement = document.activeElement

  while (activeElement?.shadowRoot && activeElement.shadowRoot?.activeElement) {
    activeElement = activeElement?.shadowRoot?.activeElement
  }

  return activeElement
}

/**
 * @returns `true` if `otherNode` is before `node` in the document; otherwise, `false`.
 */
export const isBefore = (node: DOMNode, otherNode: DOMNode): boolean =>
  Boolean(
    node.compareDocumentPosition(otherNode) & Node.DOCUMENT_POSITION_PRECEDING,
  )

/**
 * @returns `true` if `otherNode` is after `node` in the document; otherwise, `false`.
 */
export const isAfter = (node: DOMNode, otherNode: DOMNode): boolean =>
  Boolean(
    node.compareDocumentPosition(otherNode) & Node.DOCUMENT_POSITION_FOLLOWING,
  )

/**
 * Shadow DOM-aware version of Element.closest()
 * Traverses up the DOM tree, crossing shadow DOM boundaries
 */
export const closestShadowAware = (
  element: DOMElement | null | undefined,
  selector: string,
): DOMElement | null => {
  if (!element) {
    return null
  }

  let current: DOMElement | null = element

  while (current) {
    if (current.matches && current.matches(selector)) {
      return current
    }

    if (current.parentElement) {
      current = current.parentElement
    } else if (current.parentNode && 'host' in current.parentNode) {
      current = (current.parentNode as ShadowRoot).host as DOMElement
    } else {
      return null
    }
  }

  return null
}

/**
 * Shadow DOM-aware version of Node.contains()
 * Checks if a node contains another node, crossing shadow DOM boundaries
 */
export const containsShadowAware = (
  parent: DOMNode | null | undefined,
  child: DOMNode | null | undefined,
): boolean => {
  if (!parent || !child) {
    return false
  }

  if (parent.contains(child)) {
    return true
  }

  let current: DOMNode | null = child

  while (current) {
    if (current === parent) {
      return true
    }

    if (current.parentNode) {
      if ('host' in current.parentNode) {
        current = (current.parentNode as ShadowRoot).host
      } else {
        current = current.parentNode
      }
    } else {
      return false
    }
  }

  return false
}
